import { API_BASE } from "../../../lib/api.ts";

const ROOT = `${API_BASE}/service-admin/ais`;

export interface ReceiptRow {
  readonly receipt_id: string;
  readonly receipt_hash: string;
  readonly gateway_id: string;
  readonly chain_id: string;
  readonly seq: number;
  readonly kind: "call" | "aggregate" | "chain_start";
  readonly iat: number;
  readonly received_at?: number;
  readonly kid: string;
  readonly key_class?: string;
  readonly profile?: string;
  readonly policy_digest?: string | null;
  readonly policy_id?: string | null;
  readonly policy_version?: number | null;
  readonly retention_class?: string;
  readonly verified_level: "signature_only" | "chain" | "anchored";
  readonly verification_status?: string;
  readonly anchor_state?: string;
  readonly agent_sub?: string | null;
  readonly operation?: string | null;
  readonly capability?: string | null;
  readonly audience?: string | null;
  readonly outcome?: "allow" | "deny" | "error" | null;
  readonly reason?: string | null;
  readonly decision_id?: string | null;
  readonly decision_route?: "ais" | "no_decision" | "not_reached" | null;
  readonly decision_ais_outcome?: "allow" | "deny" | "unavailable" | "unverifiable" | null;
  readonly decision_attempts?: number | null;
  readonly call_id?: string | null;
  readonly started_at_ms?: number | null;
  readonly ended_at_ms?: number | null;
}
export interface ReceiptListResponse { readonly receipts: readonly ReceiptRow[]; readonly next_cursor: string | null }
export interface ReceiptDetailResponse {
  readonly receipt: ReceiptRow;
  readonly integrity: Readonly<Record<string, unknown>>;
}
export interface ChainResponse {
  readonly chain: {
    readonly chain_id: string; readonly gateway_id: string; readonly instance_id?: string;
    readonly state: "active" | "silent" | "final"; readonly high_water: number;
    readonly highest_seen?: number; readonly last_batch_at?: number | null;
    readonly last_checkpoint_iat?: number | null; readonly continues?: unknown;
  };
  readonly receipts: readonly Readonly<Record<string, unknown>>[];
  readonly gaps: readonly Readonly<Record<string, unknown>>[];
  readonly alerts: readonly Readonly<Record<string, unknown>>[];
  readonly checkpoints: readonly Readonly<Record<string, unknown>>[];
  readonly next_after_seq: number | null;
}
export interface ReceiptKeySnapshot {
  readonly v: number; readonly source: string; readonly fetched_at: number;
  readonly jwks: { readonly keys: readonly Readonly<Record<string, unknown>>[] };
  readonly key_registry: readonly {
    readonly kid: string; readonly gateway_id: string; readonly key_class: string;
    readonly valid_from: number; readonly valid_until: number; readonly revoked_at: number | null;
    readonly status: "active" | "retired" | "revoked";
  }[];
}
export interface RotationResponse {
  readonly gateway_id: string; readonly cadence_days: number; readonly reminder_days: number; readonly now: number;
  readonly keys: readonly {
    readonly kid: string; readonly key_class: string; readonly status: "active" | "retired" | "revoked";
    readonly registered_at: number; readonly valid_from: number; readonly valid_until: number;
    readonly revoked_at: number | null; readonly age_seconds: number; readonly age_days: number;
    readonly reminder_due: boolean; readonly overdue: boolean;
  }[];
}
export interface GatewayMetadata {
  readonly gateway_id: string; readonly tenant_ids: readonly string[]; readonly profile: string;
  readonly key_source: { readonly type: "jwks_uri" | "inline_jwks"; readonly uri: string | null };
}
export interface GatewayListResponse { readonly gateways: readonly GatewayMetadata[] }

export class ReceiptAdminError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(code);
    this.name = "ReceiptAdminError";
  }
}

function errorCode(body: unknown, status: number): string {
  if (typeof body !== "object" || body === null) return `http_${status}`;
  const error = (body as Record<string, unknown>)["error"];
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null && typeof (error as Record<string, unknown>)["code"] === "string") {
    return String((error as Record<string, unknown>)["code"]);
  }
  return `http_${status}`;
}

async function receiptJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${ROOT}/${path.replace(/^\/+/, "")}`, {
      credentials: "same-origin", headers: { accept: "application/json" }, ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    throw new ReceiptAdminError(0, "network");
  }
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw new ReceiptAdminError(response.status, errorCode(body, response.status));
  return body as T;
}

function queryString(values: Readonly<Record<string, string | number | undefined>>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) if (value !== undefined && value !== "") params.set(key, String(value));
  const encoded = params.toString();
  return encoded.length === 0 ? "" : `?${encoded}`;
}

export function listReceipts(filters: Readonly<Record<string, string | number | undefined>>, signal?: AbortSignal): Promise<ReceiptListResponse> {
  return receiptJson(`receipts${queryString(filters)}`, signal);
}
export function getReceipt(id: string, range: { from: string; to: string }, signal?: AbortSignal): Promise<ReceiptDetailResponse> {
  return receiptJson(`receipts/${encodeURIComponent(id)}${queryString(range)}`, signal);
}
export function getChain(chainId: string, filters: Readonly<Record<string, string | number | undefined>>, signal?: AbortSignal): Promise<ChainResponse> {
  return receiptJson(`receipt-chains/${encodeURIComponent(chainId)}${queryString(filters)}`, signal);
}
export function getReceiptKeys(gatewayId: string, signal?: AbortSignal): Promise<ReceiptKeySnapshot> {
  return receiptJson(`receipt-keys/${encodeURIComponent(gatewayId)}`, signal);
}
export function getReceiptKeyRotation(gatewayId: string, signal?: AbortSignal): Promise<RotationResponse> {
  return receiptJson(`receipt-keys/${encodeURIComponent(gatewayId)}/rotation`, signal);
}
export function getGateways(signal?: AbortSignal): Promise<GatewayListResponse> {
  return receiptJson("gateways", signal);
}

function safeFilename(header: string | null, fallback: string): string {
  if (header === null) return fallback;
  const match = /^attachment;\s*filename="([A-Za-z0-9][A-Za-z0-9._-]{0,127})"$/u.exec(header.trim());
  return match?.[1] ?? fallback;
}

export async function fetchReceiptEvidence(id: string, range: { from: string; to: string }): Promise<{ blob: Blob; filename: string }> {
  let response: Response;
  try {
    response = await fetch(`${ROOT}/receipts/${encodeURIComponent(id)}/evidence${queryString(range)}`, {
      credentials: "same-origin", headers: { accept: "application/vnd.oathera.receipt-evidence+tar, application/octet-stream" },
    });
  } catch {
    throw new ReceiptAdminError(0, "network");
  }
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    throw new ReceiptAdminError(response.status, errorCode(body, response.status));
  }
  // Do not decode, parse, or log evidence. Blob preserves the server's ustar bytes.
  const blob = await response.blob();
  return { blob, filename: safeFilename(response.headers.get("content-disposition"), `${id}-evidence.tar`) };
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Defensive public-only projection used by the JWKS download button. */
export function publicReceiptKeyExport(snapshot: ReceiptKeySnapshot): ReceiptKeySnapshot {
  return {
    v: snapshot.v,
    source: snapshot.source,
    fetched_at: snapshot.fetched_at,
    jwks: { keys: snapshot.jwks.keys.map((key) => {
      const out: Record<string, unknown> = {};
      for (const field of ["kty", "crv", "x", "kid", "use", "alg"] as const) if (key[field] !== undefined) out[field] = key[field];
      return out;
    }) },
    key_registry: snapshot.key_registry.map((key) => ({ ...key })),
  };
}

export function receiptAdminMessage(error: unknown): string {
  const code = error instanceof ReceiptAdminError ? error.code : "unknown";
  if (code === "not_found" || code === "receipt_key_not_found") return "This receipt feature is off, unavailable, or has no matching data.";
  if (code === "access_denied" || code === "service_auth_failed") return "Your role cannot read AIS audit receipts for this tenant.";
  if (code === "invalid_request") return "One or more filters are invalid or exceed the 31-day query window.";
  if (code === "evidence_unavailable") return "Raw receipt evidence is no longer available; retained integrity metadata remains.";
  if (code === "evidence_conflict") return "Evidence could not be assembled because its trust or chain material is incomplete.";
  if (code === "network") return "The request could not be sent. Check your connection and try again.";
  return "Receipt administration is temporarily unavailable.";
}
